import React from "react";
import { Provider } from "react-redux";
import { createRoot, Root } from "react-dom/client";
import { store } from "../../../../app/store";
import { BrowserRouter } from "react-router-dom";
import MobileActionsContainer from "../components/MobileActionsContainer";

// Store created roots by their container ID
const roots: Record<string, Root> = {};

/**
 * Initialize the MobileActionsContainer component
 * @param rootId - ID of the DOM element to mount the component
 */
export function hydrateMobileActionsContainer(rootId: string): void {
  // Find the root element
  const rootElement = document.getElementById(rootId);
  if (!rootElement) {
    console.error(`Failed to find root element with ID: ${rootId}`);
    return;
  }

  // Check if this container has already been initialized
  if (rootElement.dataset.reactInitialized === "true") {
    console.log(`Container ${rootId} is already initialized`);
    return;
  }

  try {
    const renderComponent = (rootInstance: Root) => {
      rootInstance.render(
        <React.StrictMode>
          <Provider store={store}>
            <BrowserRouter>
              <MobileActionsContainer />
            </BrowserRouter>
          </Provider>
        </React.StrictMode>
      );
    };

    // Check if we already have a root for this element
    if (roots[rootId]) {
      // Use the existing root instance
      console.log(`Updating existing React root for ${rootId}`);
      renderComponent(roots[rootId]);
    } else {
      // Create new root and store it
      console.log(`Creating new React root for ${rootId}`);
      const root = createRoot(rootElement);
      roots[rootId] = root;
      renderComponent(root);
    }

    // Mark container as initialized
    rootElement.dataset.reactInitialized = "true";
    console.log("MobileActionsContainer component successfully initialized");
  } catch (error) {
    console.error(
      "Error initializing MobileActionsContainer component:",
      error
    );
  }
}

// Declare global type for the hydration function
declare global {
  interface Window {
    hydrateMobileActionsContainer?: (rootId: string) => void;
  }
}

// Export the hydration function for use in the global scope
if (typeof window !== "undefined") {
  window.hydrateMobileActionsContainer = hydrateMobileActionsContainer;
}
